import { useEffect, useState, useSyncExternalStore } from 'react';
import { apiCall } from '../../utils/fetchAPI';
import { useOptionalAuth } from '../../context/AuthContext';

/**
 * Which city the reader is looking at.
 *
 * A resident said where they live when they signed up, and the API scopes
 * every read to their city without being asked — so for them this is only what
 * the header prints, and nothing is added to the requests. A visitor has said
 * nothing, so somebody has to choose: the one they picked last, else the first
 * one open. While a single city is open there is no choice to make, which is
 * why none of this shows.
 *
 * The list is fetched once per page load and remembered at module level: the
 * header mounts once, several screens ask the same question, and `/cities`
 * does not change between two of them.
 *
 * The choice is shared the same way: the header is where it is made, but every
 * screen below it reads it through its own call to this hook, and each keeping
 * its own copy meant the header changed city while the page kept showing the
 * old one until a reload.
 */
const CHOICE_KEY = 'vdq:city';

let cached = null;
let inFlight = null;

// `undefined` until first read, so a test that clears storage is read afresh.
let choice;
const listeners = new Set();

export function resetHubCityCache() {
  cached = null;
  inFlight = null;
  choice = undefined;
}

function readChoice() {
  try {
    return localStorage.getItem(CHOICE_KEY);
  } catch {
    // Private windows and blocked site data: no memory, no error.
    return null;
  }
}

function writeChoice(slug) {
  try {
    localStorage.setItem(CHOICE_KEY, slug);
  } catch {
    // Not being able to remember is not worth an error.
  }
}

function currentChoice() {
  if (choice === undefined) {
    choice = readChoice();
  }

  return choice;
}

function subscribe(listener) {
  listeners.add(listener);

  return () => listeners.delete(listener);
}

function choose(slug) {
  writeChoice(slug);
  choice = slug;
  listeners.forEach((listener) => listener());
}

async function loadCities() {
  if (cached) {
    return cached;
  }

  if (!inFlight) {
    inFlight = (async () => {
      try {
        const response = await apiCall('/cities', { method: 'GET' });
        const data = await response.json().catch(() => ({}));

        cached = data.data || [];
      } catch {
        // A name in the header is not worth an error message.
        cached = [];
      } finally {
        inFlight = null;
      }

      return cached;
    })();
  }

  return inFlight;
}

export function useCityInView() {
  const { user, isAuthenticated } = useOptionalAuth();
  const [cities, setCities] = useState(cached || []);
  const chosen = useSyncExternalStore(subscribe, currentChoice);

  useEffect(() => {
    let cancelled = false;

    loadCities().then((loaded) => {
      if (!cancelled) {
        setCities(loaded);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // A resident's own city wins: it is what the API scopes their reads to, so
  // the header showing anything else would be a lie.
  const city = isAuthenticated
    ? user?.city || cities.find((one) => one.slug === chosen) || cities[0] || null
    : cities.find((one) => one.slug === chosen) || cities[0] || null;

  return {
    city,
    cities,
    choose,
    /**
     * What to add to a request so a visitor gets the city they are looking at.
     *
     * Nothing for a resident: the API already scopes to the city on their
     * account, and sending a stale remembered slug would override it.
     */
    cityParam: !isAuthenticated && city ? city.slug : '',
  };
}

/** Just the name, for the places that only print it. */
export function useHubCity() {
  return useCityInView().city;
}
